import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import type { PlanDashboard } from '../../types/promotionPlan';
import { getPlanDashboard } from './api';

function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

function StatRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-900">{children}</span>
    </div>
  );
}

function daysRemainingLabel(d: number | null): string {
  if (d === null) return 'no end date';
  if (d < 0) return `ended ${Math.abs(d)}d ago`;
  if (d === 0) return 'ends today';
  return `${d} ${d === 1 ? 'day' : 'days'} remaining`;
}

// Spec §6. Real-time — refetched on window focus so a gate change made in
// the Rollout grid reflects here without a hard reload (smoke #27).
export function PlanDashboardStats({ planId }: { planId: string }) {
  const q = useQuery({
    queryKey: ['plan', planId, 'dashboard'],
    queryFn: () => getPlanDashboard(planId),
    refetchOnWindowFocus: true,
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) {
    return (
      <p className="text-sm text-red-600">
        Failed to load dashboard stats.
      </p>
    );
  }

  const d: PlanDashboard = q.data;
  const tasksLabel =
    d.tasksTotal === 0
      ? 'no tasks'
      : `${d.tasksCompleted} of ${d.tasksTotal} complete · ${pct(
          d.tasksCompleted,
          d.tasksTotal,
        )}%`;

  if (!d.hasRollout) {
    return (
      <div>
        <dl>
          <StatRow label="Campaigns">
            {d.campaignsAttached} attached
          </StatRow>
          <StatRow label="Locations">
            <span className="text-slate-400">
              N/A (Rollout not yet created)
            </span>
          </StatRow>
          <StatRow label="Tasks">{tasksLabel}</StatRow>
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          Create a Rollout to start tracking execution stats.
        </p>
      </div>
    );
  }

  const total = d.rowsTotal;
  return (
    <dl className="divide-y divide-slate-100">
      <StatRow label="Campaigns">
        {d.campaignsAttached} attached · {d.campaignsInRollout} in Rollout ·{' '}
        {d.campaignsNotRolled} not yet rolled
      </StatRow>
      <StatRow label="Locations">{d.locationsTotal} total</StatRow>
      <StatRow label="Tasks">{tasksLabel}</StatRow>
      <StatRow label="Meta Ready">
        {d.metaReady} of {total} ({pct(d.metaReady, total)}%)
      </StatRow>
      <StatRow label="Google Ready">
        {d.googleReady} of {total} ({pct(d.googleReady, total)}%)
      </StatRow>
      <StatRow label="Local Built">
        {d.localBuilt} of {total} ({pct(d.localBuilt, total)}%)
      </StatRow>
      <StatRow label="Status">
        <span className="capitalize">{d.rolloutStatus}</span>
        {' · '}
        {daysRemainingLabel(d.daysRemaining)}
      </StatRow>
    </dl>
  );
}
